import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BRAND_LOGO } from '../utils/constants';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', isRoute: true },
    { label: 'Our Properties', href: isHomePage ? '#properties' : '/#properties', isRoute: false },
    { label: 'Events & Weddings', href: isHomePage ? '#events' : '/#events', isRoute: false },
    { label: '101 Bar & Lounge', href: '/bar-lounge', isRoute: true },
    { label: 'Testimonials', href: isHomePage ? '#testimonials' : '/#testimonials', isRoute: false },
    { label: 'Contact', href: isHomePage ? '#contact' : '/#contact', isRoute: false }
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-navy-900/95 backdrop-blur-md shadow-lg py-4' 
          : 'bg-transparent py-6'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center">
            {/* Logo - Left */}
            <Link to="/" className="absolute left-4 lg:left-8 flex items-center space-x-3 group">
              <img 
                src={BRAND_LOGO} 
                alt="Jalsa Group" 
                className={`transition-all duration-500 ${
                  isScrolled ? 'h-12' : 'h-16'
                } w-auto drop-shadow-2xl`}
              />
            </Link>

            {/* Desktop Navigation - Centered */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link, index) => (
                link.isRoute ? (
                  <Link
                    key={index}
                    to={link.href}
                    className="text-white font-light text-sm tracking-widest hover:text-gold-400 transition-colors duration-300 relative group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-400 group-hover:w-full transition-all duration-300" />
                  </Link>
                ) : (
                  <a
                    key={index}
                    href={link.href}
                    className="text-white font-light text-sm tracking-widest hover:text-gold-400 transition-colors duration-300 relative group"
                  >
                    {link.label}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gold-400 group-hover:w-full transition-all duration-300" />
                  </a>
                )
              ))}
            </div>

            {/* Mobile Menu Button - Right */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden absolute right-4 text-white hover:text-gold-400 transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-navy-900/95 backdrop-blur-md"
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Menu Content */}
        <div className={`absolute top-24 right-4 left-4 bg-gradient-to-b from-navy-800 to-navy-900 border border-gold-400/20 rounded-lg p-8 transition-all duration-500 ${
          isMobileMenuOpen ? 'translate-y-0' : '-translate-y-10'
        }`}>
          {/* Navigation Links */}
          <div className="space-y-6 mb-8">
            {navLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-white text-lg font-light tracking-wider hover:text-gold-400 transition-colors duration-300 hover:translate-x-2 transform"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
